import * as React from 'react';
import {
  StepContentTransitionHorizontal,
  StepOrientationProps,
  StepperContent,
  StepperContentBody,
  TypeTransitionProps,
} from './stepper-styled';

export interface StepRenderContentProps {
  gotoStep: (step: number) => void;
  gotoPrev: () => void;
  gotoNext: () => void;
  canGotoPrev: boolean;
  canGotoNext: boolean;
}

export interface StepContentDefinition {
  content?: (props: StepRenderContentProps) => JSX.Element;
  isDisabled?: boolean;
}

export type StepContentCollectionItem = StepContentDefinition & {
  resumeOnClosed?: string;
};

const EMPTY_STEP_CONTENT = () => <React.Fragment />;

export interface StepContentProps
  extends Pick<
    StepRenderContentProps,
    'canGotoNext' | 'canGotoPrev' | 'gotoStep'
  > {
  steps: StepContentCollectionItem[];
  currentStep: number;
  preserveContent?: boolean;
  resumeOnClosed?: string;
  content: NonNullable<StepContentDefinition['content']>;
  onNext: () => void;
  onPrev: () => void;
}

interface StepperContentToRenderProps extends StepOrientationProps {
  transition: TypeTransitionProps;
  steps: StepContentCollectionItem[];
  currentStep: number;
  onStepChange(step: number): void;
  preserveContent?: boolean;
}

export const StepperContentToRender: React.FC<StepperContentToRenderProps> = ({
  orientation,
  transition,
  steps,
  currentStep,
  onStepChange,
  preserveContent = false,
}) => {
  const content = steps[currentStep - 1].content;
  const hasContent = preserveContent
    ? steps.some((step) => typeof step.content === 'function')
    : typeof content === 'function';

  return (
    <>
      {hasContent && (
        <StepperContent
          as="section"
          className="BaSe--stepper-content"
          orientation={orientation}
          transition={transition}
        >
          <StepContentTransitionHorizontal transition={transition}>
            <BaSeStepContent
              steps={steps}
              content={content ?? EMPTY_STEP_CONTENT}
              canGotoNext={
                currentStep < steps.length && !steps?.[currentStep]?.isDisabled
              }
              canGotoPrev={
                currentStep > 1 && !steps?.[currentStep - 2]?.isDisabled
              }
              gotoStep={onStepChange}
              onNext={() => onStepChange(currentStep + 1)}
              onPrev={() => onStepChange(currentStep - 1)}
              currentStep={currentStep}
              preserveContent={preserveContent}
            />
          </StepContentTransitionHorizontal>
        </StepperContent>
      )}
    </>
  );
};

export const BaSeStepContent: React.FC<StepContentProps> = ({
  canGotoNext,
  canGotoPrev,
  steps,
  currentStep,
  preserveContent = false,
  content,
  onNext,
  onPrev,
  gotoStep,
}) => {
  if (preserveContent) {
    return (
      <>
        {steps &&
          steps.map((step, index) => {
            return (
              <StepperContentBody
                key={index}
                hidden={index + 1 !== currentStep}
              >
                {step &&
                  step.content &&
                  step?.content({
                    gotoPrev: onPrev,
                    gotoNext: onNext,
                    canGotoPrev,
                    canGotoNext,
                    gotoStep,
                  })}
              </StepperContentBody>
            );
          })}
      </>
    );
  }

  return (
    <>
      {content({
        gotoPrev: onPrev,
        gotoNext: onNext,
        canGotoPrev,
        canGotoNext,
        gotoStep,
      })}
    </>
  );
};

BaSeStepContent.displayName = 'BaSeStepContent';

StepperContentToRender.displayName = 'StepperContentToRender';
